'use client';

import { useState } from 'react';
import {
  EyeIcon,
  EyeSlashIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { logger } from '@/lib/platform/utils/logger';

interface SecretInputProps {
  label: string;
  value: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  error?: string;
  helperText?: string;
  showCopyButton?: boolean; // If false, only View->Hide. If true, View->Copy->Hide
}

export default function SecretInput({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  readonly = false,
  error,
  helperText,
  showCopyButton = true,
}: SecretInputProps) {
  const { darkMode } = useDarkMode();
  const [isVisible, setIsVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error('Failed to copy:', err);
    }
  };

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  return (
    <div>
      <label
        className={`mb-2 block text-[13px] font-medium ${
          darkMode ? 'text-gray-400' : 'text-gray-600'
        }`}
      >
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>

      <div className="relative">
        <input
          type={isVisible ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          readOnly={readonly}
          className={`block w-full rounded-md px-3 py-1.5 pr-20 text-base outline-1 -outline-offset-1 transition-all sm:text-sm/6 ${
            darkMode
              ? 'bg-white/5 text-gray-100 outline-white/10 placeholder:text-gray-500'
              : 'bg-white text-gray-900 outline-gray-300 placeholder:text-gray-400'
          } ${
            darkMode
              ? 'focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-500'
              : 'focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600'
          } ${
            error
              ? darkMode
                ? 'outline-red-500/50 focus:outline-red-500'
                : 'outline-red-500 focus:outline-red-500'
              : ''
          } ${
            disabled ? 'disabled:cursor-not-allowed disabled:opacity-60' : ''
          } ${readonly ? 'cursor-default' : ''} `}
        />

        <div className="absolute top-1/2 right-2 flex -translate-y-1/2 items-center gap-1">
          {/* Copy Button (if enabled) */}
          {showCopyButton && value && (
            <button
              type="button"
              onClick={handleCopy}
              disabled={disabled}
              className={`rounded-md p-1.5 transition-colors ${
                darkMode
                  ? 'text-gray-400 hover:bg-gray-600 hover:text-gray-100'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              } disabled:cursor-not-allowed disabled:opacity-50`}
              title={copied ? 'Copied!' : 'Copy to clipboard'}
            >
              {copied ? (
                <CheckIcon className="h-4 w-4 text-green-500" />
              ) : (
                <ClipboardDocumentIcon className="h-4 w-4" />
              )}
            </button>
          )}

          {/* View/Hide Button */}
          <button
            type="button"
            onClick={toggleVisibility}
            disabled={disabled}
            className={`rounded-md p-1.5 transition-colors ${
              darkMode
                ? 'text-gray-400 hover:bg-gray-600 hover:text-gray-100'
                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            } disabled:cursor-not-allowed disabled:opacity-50`}
            title={isVisible ? 'Hide' : 'View'}
          >
            {isVisible ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Error Message or Helper Text */}
      {(error || helperText) && (
        <p
          className={`mt-2 text-sm ${
            error ? 'text-red-500' : darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
}
